import React, { useEffect, useState } from 'react';
import { useI18n } from '../i18n/i18nContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { galleryAPI, GalleryImage, getImageUrl } from '../services/api';
import Lightbox from '../components/Lightbox';
import SEO from '../components/SEO';

const GalleryPage: React.FC = () => {
  const { t, language } = useI18n();
  useDocumentTitle(t.gallery?.pageTitle || t.pageTitles?.gallery || 'Gallery');

  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        setLoading(true);
        const response = await galleryAPI.getByType('grid');
        if (response.data?.images) {
          setImages(response.data.images);
        }
      } catch (err) {
        console.error('Error fetching gallery images:', err);
        setError(t.gallery?.loadError || 'Failed to load gallery');
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, [t.gallery?.loadError]);

  const handleImageClick = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const handleLightboxClose = () => {
    setLightboxOpen(false);
  };

  const getDescription = (image: GalleryImage) => {
    if (!image.descriptions) return '';
    const descriptions = image.descriptions;
    return descriptions[language as keyof typeof descriptions] ||
           descriptions.en ||
           descriptions.et ||
           '';
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16 max-w-6xl">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">{t.common.loading}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-16 max-w-6xl">
        <div className="flex justify-center items-center h-64">
          <div className="text-red-500">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title={t.gallery?.pageTitle || 'Gallery'}
        description={t.gallery?.pageDescription || t.seo?.galleryDescription || 'Browse our gallery of wellness experiences at Fire & Ice.'}
      />

      <main className="flex-grow">
        <div className="container mx-auto px-4 py-16 max-w-6xl">
          <h1 className="text-3xl md:text-4xl font-bold text-center mb-4">
            {t.gallery?.title || 'Gallery'}
          </h1>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            {t.gallery?.subtitle || 'Explore moments from our wellness studio'}
          </p>

          {images.length === 0 ? (
            <div className="text-center text-gray-500 py-16">
              {t.gallery?.noImages || 'No images available'}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {images.map((image, index) => (
                <div
                  key={image.id}
                  className="relative aspect-square overflow-hidden rounded-lg shadow-md cursor-pointer group"
                  onClick={() => handleImageClick(index)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleImageClick(index);
                    }
                  }}
                  aria-label={getDescription(image) || t.gallery?.viewImage || 'View image'}
                >
                  <img
                    src={getImageUrl(image.thumbnail_url || image.image_url)}
                    alt={getDescription(image) || ''}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    loading="lazy"
                  />
                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <svg
                        className="w-10 h-10 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Lightbox
        images={images}
        currentIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={handleLightboxClose}
        onNavigate={setLightboxIndex}
      />
    </div>
  );
};

export default GalleryPage;
